import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaToggleOn, FaToggleOff, FaUserAlt, FaSignOutAlt, FaBars, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { logoutDriver, clearDriverAuth } from '../features/driver/driverAuthSlice';
import { updateDriverAvailability, getDriverProfile } from '../features/driver/driverSlice';
import socketService from '../utils/socketService';
import { isObjectId } from '../utils/objectIdUtils';

const DriverHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Get driver data from both auth and profile state
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver: driverData } = useSelector((state) => state.driver);
  
  // Ensure driver profile is loaded and refresh periodically to get latest availability status
  useEffect(() => {
    if (driverAuth && !driverData) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        dispatch(getDriverProfile(driverId));
      }
    }
  }, [driverAuth, driverData, dispatch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Refresh driver profile every 30 seconds to ensure availability status is current
  useEffect(() => {
    if (driverAuth) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        const interval = setInterval(() => {
          dispatch(getDriverProfile(driverId));
        }, 30000); // Refresh every 30 seconds

        return () => clearInterval(interval);
      }
    }
  }, [driverAuth, dispatch]);

  // Listen for ride completion events to immediately update driver status
  useEffect(() => {
    if (driverAuth) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        // Connect to socket
        socketService.connect();

        // Listen for ride completion events
        socketService.onRideCompleted((data) => {
          if (data.driverId === driverId) {
            // Immediately refresh driver profile to get updated availability status
            dispatch(getDriverProfile(driverId));
          }
        });

        return () => {
          socketService.disconnect();
        };
      }
    }
  }, [driverAuth, dispatch]);
  
  // Get the driver name from any available source
  const driverName = driverData?.name || driverAuth?.name || driverAuth?.driver?.name || 'Driver';

  // Debug logging to track driver data sources and validate driver ID
  useEffect(() => {

    // Validate driver ID format and clear session if invalid
    if (driverData?._id) {
      const driverId = String(driverData._id);
      if (!isObjectId(driverId)) {
        console.error('❌ Invalid driver ID format detected:', driverId);
        dispatch(clearDriverAuth());
        navigate('/driver/login', { replace: true });
      }
    }
  }, [driverAuth, driverData, driverName, dispatch, navigate]);
  
  // Function to toggle driver availability status
  const toggleAvailability = async () => {
    // Get driver ID from available sources (prioritize driverData, fallback to driverAuth)
    const driverId = driverData?._id || driverAuth?._id || driverAuth?.driver?._id;

    if (!driverId) {
      console.error('❌ Cannot toggle availability: No driver ID found', {
        driverData: !!driverData,
        driverDataId: driverData?._id,
        driverAuth: !!driverAuth,
        driverAuthId: driverAuth?._id,
        driverAuthDriverId: driverAuth?.driver?._id
      });
      toast.error('Unable to update availability. Please refresh the page.');
      return;
    }

    // Validate driver ID format
    if (!isObjectId(String(driverId))) {
      console.error('❌ Invalid driver ID format:', driverId);
      toast.error('Invalid driver session. Please log in again.');
      dispatch(clearDriverAuth());
      navigate('/driver/login');
      return;
    }

    const currentAvailability = driverData?.isAvailable ?? false;

  
    setLoading(true);
    try {
      const result = await dispatch(updateDriverAvailability({
        driverId,
        isAvailable: !currentAvailability
      }));

      if (result.type.endsWith('/rejected')) {
        console.error('❌ Failed to update availability:', result.payload);

        // Handle specific error cases
        if (result.payload && result.payload.includes('Driver not found')) {
          // Vendor drivers may intermittently return 'Driver not found' while backend creates records.
          // Treat as non-fatal: retry profile fetch silently and show a gentle warning instead of logging out.
          console.warn('Driver not found when toggling availability. Will retry fetching profile.');
          toast.warn('Temporary issue updating availability — retrying silently.');
          // Retry profile fetch after a short delay
          setTimeout(() => {
            const driverId = driverAuth?._id || (driverAuth && driverAuth.driver && driverAuth.driver._id);
            if (driverId) dispatch(getDriverProfile(driverId));
          }, 5000);
        } else if (result.payload && result.payload.includes('No authentication token')) {
          toast.error('Session expired. Please log in again.');
          dispatch(clearDriverAuth());
          navigate('/driver/login');
        } else {
          toast.error(result.payload || 'Failed to update availability');
        }
      } else {
        toast.success(`You are now ${!currentAvailability ? 'available' : 'unavailable'} for rides`);
      }
    } catch (error) {
      console.error('❌ Error toggling availability:', error);
      toast.error('An error occurred while updating availability');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {

    dispatch(clearDriverAuth()); // Use clearDriverAuth instead of logoutDriver for complete cleanup
    dispatch(logoutDriver());
    navigate('/driver/login');
  };

  return (
    <header className="bg-gradient-to-r from-primary to-secondary py-4 text-white shadow-md">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="text-2xl font-bold mr-2">
            <span className="text-white">Driver</span>
            <span className="text-yellow-300">Dashboard</span>
          </div>
          {driverData && driverData.isApproved && (
            <div className="ml-4 flex items-center space-x-3">
              {/* Driver Type Badge - Only for vendor drivers */}
              {driverData.isVendorDriver && (
                <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500 bg-opacity-80">
                  🏢 Vendor Driver
                </div>
              )}

              {/* Availability Toggle */}
              <div className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full">
                <span className="mr-2 text-sm font-medium">
                  {driverData.isAvailable ? 'Available' : 'Unavailable'}
                </span>
                <button
                  onClick={toggleAvailability}
                  disabled={loading}
                  className="text-xl focus:outline-none transition-colors"
                  aria-label={driverData.isAvailable ? "Set unavailable" : "Set available"}
                >
                  {driverData.isAvailable ? (
                    <FaToggleOn className="text-green-300" />
                  ) : (
                    <FaToggleOff className="text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center">
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/driver/current-ride" className="hover:text-yellow-300 transition-colors">
              Current Ride
            </Link>
            <Link to="/driver/ride-history" className="hover:text-yellow-300 transition-colors">
              Ride History
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 hover:text-yellow-300 transition-colors"
              >
                <FaUserAlt className="mr-1" />
                <span>{driverName}</span>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <FaSignOutAlt className="inline mr-2" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden text-white focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white absolute left-0 right-0 z-50 shadow-md">
          <div className="px-4 py-2">
            <Link
              to="/driver/current-ride"
              className="block py-2 text-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Current Ride
            </Link>
            <Link 
              to="/driver/ride-history" 
              className="block py-2 text-gray-700"
              onClick={() => setIsMenuOpen(false)}
            >
              Ride History
            </Link>
            <button 
              onClick={() => {
                handleLogout();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 text-gray-700"
            >
              <FaSignOutAlt className="inline mr-2" /> Logout
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default DriverHeader;
